import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { useSnackbar } from 'notistack'
import { getErrorMessage, parseQueryParams } from '../../../utilities/parsers'
import { QueryParams } from '../../../common/types'
import apiUrl from '../../../apis/api.url'
import {
  deleteData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../../apis/api.helpers'
import { useSnackbarManager } from '../../../components/common/snackbar'

const buildUrlWithParams = (baseUrl: string, params?: Partial<QueryParams>) => {
  return `${baseUrl}${parseQueryParams(params || ({} as any))}`
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_INSTITUTIONS, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useAdminUser = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchData(input), {})
}

const customerMode = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.CUSTOMER_MODE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useCustomerMode = (input: QueryParams) => {
  return useQuery(['customer_mode', input], () => customerMode(input), {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_INSTITUTIONS}/${id}`)
}

const adminDetails = async (id: string) => {
  const url = `${apiUrl.ADMIN_INSTITUTIONS}/${id}`
  const response = await getData(url)
  return response
}
export const useAdminDetails = (id: string) => {
  return useQuery(['worker_task', id], () => adminDetails(id), {
    refetchOnWindowFocus: false,
  })
}

export const createAcademy = (input: any) => {
  return postData(`${apiUrl.INDUSTRY_ACADEMIA_COLLABRARTION}`, input)
}
export const useCreateAcademy = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(createAcademy, {
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('Academy created successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(getErrorMessage(err?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const assignTemplate = (input: any) => {
  return postData(`${apiUrl.TEMPLATE_ASSIGN}`, input)
}
export const useAssignTemplate = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(assignTemplate, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Template Assigned Successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}
const fetchDistrict = async () => {
  const url = buildUrlWithParams(apiUrl.DISTRICT_LIST)
  const response = await getData(url)
  return response
}
export const useDistrictList = () => {
  return useQuery(['district_listing'], () => fetchDistrict(), {
    refetchOnWindowFocus: false,
  })
}
export const editCategory = (data: { id: number; input: any }) => {
  return updateData(`${apiUrl.ADMIN_INSTITUTIONS}/${data.id}`, {
    user: data.input,
  })
}

export const useUpdateAdmin = (
  handleSubmission: (data: any) => void // Simplify this if `id` isn't needed separately
) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(editCategory, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)

      enqueueSnackbar('Customer Edited Successfully', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}

// Profile update (common for all roles)
export const updateProfile = (payload: any) => {
  return postData(apiUrl.PROFILE_UPDATE, payload)
}

export const useUpdateProfile = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(updateProfile, {
    onSuccess: (res: any) => {
      onSuccessHandler(res?.data)
      enqueueSnackbar('Profile updated successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}
